import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { provideNativeDateAdapter } from '@angular/material/core';
import { PatientFilterComponent } from '../shared/patient-filter/patient-filter.component';
import { FhirResourceService } from '../shared/fhir-resource.service';
import { authBackendEndPointToken, backendEndPointToken, appointmentTypesToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { HttpClient, HttpContext } from '@angular/common/http';
import { LoadingUIEnabled } from '../loading.interceptor';
import { UtilityService } from '../shared/utility.service';
import { map } from 'rxjs/operators';
import { Practitioner, Slot, Appointment, PractitionerRole, Bundle } from 'fhir/r4';
import { ErrorService } from '../shared/error.service';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../shared/auth/auth.service';
import { Observable, EMPTY, forkJoin, of, Subject } from 'rxjs';
import { expand, reduce, catchError, switchMap, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../shared/confirmation-dialog/confirmation-dialog.component';
import { FirebaseAlertService } from '../shared/services/firebase-alert.service';

export interface PractitionerOption {
  id: string;
  name: string;
  roleDisplay: string;
}

@Component({
  selector: 'app-make-appointments',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatCardModule, MatButtonModule,
    MatSelectModule, MatAutocompleteModule, MatDatepickerModule, MatIconModule, MatInputModule, MatChipsModule,
    PatientFilterComponent
  ],
  templateUrl: './make-appointments.component.html',
  styleUrl: './make-appointments.component.scss',
  providers: [provideNativeDateAdapter()]
})
export class MakeAppointmentsComponent implements OnInit {
  fb = inject(FormBuilder);
  fhirResourceService = inject(FhirResourceService);
  backendUrl = inject(backendEndPointToken);
  authBackendUrl = inject(authBackendEndPointToken);
  appointmentTypes = inject(appointmentTypesToken);
  errorService = inject(ErrorService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  http = inject(HttpClient);
  utilityService = inject(UtilityService);
  authService = inject(AuthService);
  terminologyUrl = inject(terminologyCanonicalUrlBaseToken);
  dialog = inject(MatDialog);
  firebaseAlertService = inject(FirebaseAlertService);
  
  bookingForm: FormGroup;
  practitionerSearchCtrl = new FormControl();
  practitioners: PractitionerOption[] = [];
  filteredPractitioners: PractitionerOption[] = [];
  existingAppointments: Appointment[] = [];
  workingSlots: Slot[] = [];
  shiftBlocks: {startMs: number, endMs: number}[] = [];
  availableBlocks: {startStr: string, endStr: string, startMs: number, endMs: number}[] = [];
  loadingSchedule = false;
  overlapError = '';
  selectedPatientId: string | null = null;
  loadingPractitioners = false;
  
  private scheduleFetchSubject = new Subject<{practitionerId: string, dateStr: string}>();
  
  // Filtered appointment types for the receptionist
  allowedAppointmentTypes: any[] = [];

  constructor() {
    this.bookingForm = this.fb.group({
      practitioner: ['', Validators.required],
      appointmentType: ['', Validators.required],
      date: [new Date(), Validators.required],
      time: ['', Validators.required],
      duration: [15, [Validators.required, Validators.min(1)]]
    });
  }

  ngOnInit() {
    this.scheduleFetchSubject.pipe(
      tap(() => this.loadingSchedule = true),
      switchMap(({practitionerId, dateStr}) => 
        forkJoin({
          appointments: this.fetchAppointmentsForDay(practitionerId, dateStr),
          slots: this.fetchSlotsForDay(practitionerId, dateStr)
        })
      )
    ).subscribe({
      next: (results) => {
        this.existingAppointments = results.appointments;
        this.workingSlots = results.slots;
        this.calculateAvailableBlocks();
        this.loadingSchedule = false;
        this.checkOverlap();
      },
      error: (err) => {
        console.error('[DEBUG] Failed to fetch schedule', err);
        this.loadingSchedule = false;
      }
    });

    this.loadPractitioners();

    this.practitionerSearchCtrl.valueChanges.subscribe(val => {
      const search = (typeof val === 'string' ? val : val?.name)?.toLowerCase() || '';
      this.filteredPractitioners = this.practitioners.filter(p => 
        p.name.toLowerCase().includes(search) || p.roleDisplay.toLowerCase().includes(search)
      );
    });
    
    // Restrict appointment types to typical outpatient flows for the Receptionist
    const allowedCodes = ['patient-consultation', 'follow-up', 'routine-checkup']; 
    this.allowedAppointmentTypes = this.appointmentTypes.filter((t: any) => allowedCodes.includes(t.code));

    // If the original tokens didn't have these specific codes, fallback to whatever is available 
    // but exclude explicitly internal things like 'ward-rounds' if possible.
    if (this.allowedAppointmentTypes.length === 0) {
       this.allowedAppointmentTypes = this.appointmentTypes.filter((t: any) => !t.code.includes('ward') && !t.code.includes('surgery'));
    }

    this.route.queryParams.subscribe(params => {
      this.selectedPatientId = params['patientId'] || null;
    });

    // Set default duration when appointment type changes
    this.bookingForm.get('appointmentType')?.valueChanges.subscribe(typeCode => {
      console.log('[DEBUG] appointmentType value changed:', typeCode);
      const typeObj = this.appointmentTypes.find((t: any) => t.code === typeCode);
      if (typeObj && typeObj.defaultDurationMinutes) {
        this.bookingForm.patchValue({ duration: typeObj.defaultDurationMinutes }, { emitEvent: false });
      }
      this.fetchDaySchedule();
    });

    // Fetch schedule when practitioner or date changes
    this.bookingForm.get('practitioner')?.valueChanges.subscribe((val) => {
      console.log('[DEBUG] practitioner value changed:', val);
      this.fetchDaySchedule();
    });
    this.bookingForm.get('date')?.valueChanges.subscribe((val) => {
      console.log('[DEBUG] date value changed:', val);
      this.fetchDaySchedule();
    });
    
    // Check for overlap when time or duration changes
    this.bookingForm.get('time')?.valueChanges.subscribe(() => this.checkOverlap());
    this.bookingForm.get('duration')?.valueChanges.subscribe(() => this.checkOverlap());
  }
  
  displayPractitionerFn(p?: PractitionerOption): string {
    return p ? `${p.name} (${p.roleDisplay})` : '';
  }

  onPractitionerSelected(event: any) {
    this.bookingForm.patchValue({ practitioner: event.option.value.id });
  }

  onPatientSelected(patientId: string) {
    this.selectedPatientId = patientId;
  }

  private loadPractitioners() {
    this.loadingPractitioners = true;
    const ctx = new HttpContext().set(LoadingUIEnabled, false);
    
    // Explicitly scope by organization to ensure server-side filtering handles pagination correctly.
    // The loading interceptor would catch unscoped requests via client-side filtering, 
    // but that could result in empty pages if the first 200 roles belong to other hospitals.
    const user: any = this.authService.user.getValue();
    const orgId = user?.organizationId;
    
    let url = `${this.backendUrl}/PractitionerRole?_include=PractitionerRole:practitioner&_count=200`;
    if (orgId) {
      url += `&organization=Organization/${orgId}`;
    }

    // Use raw HTTP and RxJS expand to handle pagination manually.
    // We cannot use utilityService.fetchAll here because it actively strips out
    // the included Practitioner resources (it strictly filters by resourceType).
    this.http.get<Bundle<any>>(url, { context: ctx }).pipe(
      expand((bundle, pageIndex) => {
        if (pageIndex + 1 >= 100) return EMPTY; // Max 100 pages
        const nextLink = (bundle?.link ?? []).find(
          (link) => String(link?.relation ?? '').toLowerCase() === 'next'
        )?.url;
        const nextUrl = this.utilityService.normalizeNextLinkUrl(nextLink ?? null, url);
        return nextUrl ? this.http.get<Bundle<any>>(nextUrl, { context: ctx }) : EMPTY;
      }),
      map(bundle => (bundle?.entry ?? []).map(entry => entry.resource).filter(Boolean)),
      reduce((acc, curr) => acc.concat(curr), [] as any[])
    )
    .subscribe({
        next: (resources) => {
          const roles = resources.filter((r: any) => r.resourceType === 'PractitionerRole') as PractitionerRole[];
          const practs = resources.filter((r: any) => r.resourceType === 'Practitioner') as Practitioner[];
          
          const options: PractitionerOption[] = [];
          
          const ROLE_MAP: Record<string, string> = {
            'alliedprofessionalhealth': 'Allied Professional Health',
            'generalpractitioner': 'General Practitioner',
            'specialist': 'Specialist',
            'nursemidwife': 'Nurse / Midwife',
            'surgeon': 'Surgeon',
            'healthcareassistant': 'Healthcare Assistant',
            'doctor': 'Doctor',
            'primaryhealthcareprovider': 'Primary Care Provider'
          };
          
          const allowedRoles = ['doctor', 'generalpractitioner', 'specialist', 'nursemidwife', 'healthcareassistant', 'surgeon', 'primaryhealthcareprovider'];
          
          roles.forEach(role => {
            const pRef = role.practitioner?.reference;
            if (pRef) {
              const pId = pRef.replace('Practitioner/', '');
              const p = practs.find(pract => pract.id === pId);
              if (p) {
                const name = this.getPractitionerDisplay(p);
                let rawRole = role.code?.[0]?.coding?.[0]?.display || role.code?.[0]?.text || 'General Practitioner';
                
                const lowerRole = rawRole.toLowerCase().replace(/[^a-z]/g, '');
                if (allowedRoles.includes(lowerRole) || rawRole.toLowerCase().includes('doctor') || rawRole.toLowerCase().includes('nurse')) {
                    const roleDisplay = ROLE_MAP[lowerRole] || rawRole;
    
                    options.push({
                      id: p.id!,
                      name: name,
                      roleDisplay: roleDisplay
                    });
                }
              }
            }
          });
          
          // Remove duplicates if a practitioner has multiple roles
          this.practitioners = options.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i);
          this.filteredPractitioners = [...this.practitioners];
          this.loadingPractitioners = false;
        },
        error: () => this.loadingPractitioners = false
    });
  }

  getPractitionerDisplay(practitioner: Practitioner): string {
    const name = practitioner.name?.[0];
    const prefix = Array.isArray(name?.prefix) ? name?.prefix?.join(' ') : '';
    const given = Array.isArray(name?.given) ? name?.given?.join(' ') : '';
    const family = name?.family ?? '';
    return `${prefix} ${given} ${family}`.trim() || 'Unnamed Practitioner';
  }

  fetchAppointmentsForDay(practitionerId: string, dateStr: string): Observable<Appointment[]> {
    const actor = encodeURIComponent(`Practitioner/${practitionerId}`);
    const startGe = encodeURIComponent(`${dateStr}T00:00:00Z`);
    const startLe = encodeURIComponent(`${dateStr}T23:59:59Z`);
    const status = encodeURIComponent('booked,arrived,fulfilled,in-progress,tentative');
    const queries = `actor=${actor}&date=ge${startGe}&date=le${startLe}&status=${status}`;
    const url = `${this.backendUrl}/Appointment?${queries}`;
    const ctx = new HttpContext().set(LoadingUIEnabled, false);
    
    return this.http.get<Bundle<any>>(url, { context: ctx }).pipe(
      expand((bundle, pageIndex) => {
        if (pageIndex + 1 >= 50) return EMPTY; // Max 50 pages
        const nextLink = (bundle?.link ?? []).find((link) => String(link?.relation ?? '').toLowerCase() === 'next')?.url;
        const nextUrl = this.utilityService.normalizeNextLinkUrl(nextLink ?? null, url);
        return nextUrl ? this.http.get<Bundle<any>>(nextUrl, { context: ctx }) : EMPTY;
      }),
      map(bundle => (bundle?.entry ?? []).map(entry => entry.resource).filter(Boolean)),
      reduce((acc, curr) => acc.concat(curr), [] as any[]),
      map(resources => resources.filter((r: any) => r.resourceType === 'Appointment') as Appointment[]),
      catchError(() => of([]))
    );
  }

  fetchSlotsForDay(practitionerId: string, dateStr: string): Observable<Slot[]> {
    const actor = encodeURIComponent(`Practitioner/${practitionerId}`);
    const startGe = encodeURIComponent(`${dateStr}T00:00:00Z`);
    const startLe = encodeURIComponent(`${dateStr}T23:59:59Z`);
    const queries = `schedule.actor=${actor}&start=ge${startGe}&start=le${startLe}&status=free`;
    const url = `${this.backendUrl}/Slot?${queries}`;
    const ctx = new HttpContext().set(LoadingUIEnabled, false);
    
    return this.http.get<Bundle<any>>(url, { context: ctx }).pipe(
      expand((bundle, pageIndex) => {
        if (pageIndex + 1 >= 50) return EMPTY;
        const nextLink = (bundle?.link ?? []).find((link) => String(link?.relation ?? '').toLowerCase() === 'next')?.url;
        const nextUrl = this.utilityService.normalizeNextLinkUrl(nextLink ?? null, url);
        return nextUrl ? this.http.get<Bundle<any>>(nextUrl, { context: ctx }) : EMPTY;
      }),
      map(bundle => (bundle?.entry ?? []).map(entry => entry.resource).filter(Boolean)),
      reduce((acc, curr) => acc.concat(curr), [] as any[]),
      map(resources => resources.filter((r: any) => r.resourceType === 'Slot') as Slot[]),
      catchError(() => of([]))
    );
  }

  fetchDaySchedule() {
    // Read directly from the controls rather than the parent FormGroup's `.value` object.
    // When FormGroup.patchValue() is used, child valueChanges fire BEFORE the parent FormGroup
    // completes aggregating its overall `.value` object.
    const practitionerId = this.bookingForm.get('practitioner')?.value;
    const dateObj = this.bookingForm.get('date')?.value;
    if (!practitionerId || !dateObj) {
      this.existingAppointments = [];
      this.workingSlots = [];
      this.availableBlocks = [];
      this.shiftBlocks = [];
      this.overlapError = '';
      return;
    }

    const isoDate = typeof dateObj.toISOString === 'function' ? dateObj.toISOString() : new Date(dateObj).toISOString();
    const dateStr = isoDate.split('T')[0];
    
    console.log('[DEBUG] Triggering fetchDaySchedule for:', { practitionerId, dateStr });
    this.scheduleFetchSubject.next({ practitionerId, dateStr });
  }

  calculateAvailableBlocks() {
    const workingBlocks: {startMs: number, endMs: number}[] = [];
    const sortedSlots = [...this.workingSlots].filter(s => s.start && s.end).sort((a, b) => new Date(a.start!).getTime() - new Date(b.start!).getTime());
    
    if (sortedSlots.length === 0) {
      this.shiftBlocks = [];
      this.availableBlocks = [];
      return;
    }
    
    let currentBlock = { 
      startMs: new Date(sortedSlots[0].start!).getTime(), 
      endMs: new Date(sortedSlots[0].end!).getTime() 
    };
    
    for (let i = 1; i < sortedSlots.length; i++) {
      const slot = sortedSlots[i];
      const startMs = new Date(slot.start!).getTime();
      const endMs = new Date(slot.end!).getTime();
      
      if (startMs <= currentBlock.endMs) {
        currentBlock.endMs = Math.max(currentBlock.endMs, endMs);
      } else {
        workingBlocks.push(currentBlock);
        currentBlock = { startMs, endMs };
      }
    }
    workingBlocks.push(currentBlock);
    
    this.shiftBlocks = workingBlocks;
    let freeBlocks = [...workingBlocks];
    
    for (const appt of this.existingAppointments) {
      if (!appt.start || !appt.end) continue;
      const apptStart = new Date(appt.start).getTime();
      const apptEnd = new Date(appt.end).getTime();
      
      const newFreeBlocks: {startMs: number, endMs: number}[] = [];
      
      for (const block of freeBlocks) {
        if (apptStart <= block.startMs && apptEnd >= block.endMs) {
          continue;
        } else if (apptStart > block.startMs && apptEnd < block.endMs) {
          newFreeBlocks.push({ startMs: block.startMs, endMs: apptStart });
          newFreeBlocks.push({ startMs: apptEnd, endMs: block.endMs });
        } else if (apptStart <= block.startMs && apptEnd > block.startMs) {
          newFreeBlocks.push({ startMs: apptEnd, endMs: block.endMs });
        } else if (apptStart < block.endMs && apptEnd >= block.endMs) {
          newFreeBlocks.push({ startMs: block.startMs, endMs: apptStart });
        } else {
          newFreeBlocks.push(block);
        }
      }
      freeBlocks = newFreeBlocks;
    }
    
    this.availableBlocks = freeBlocks
      .filter(b => b.endMs > b.startMs)
      .map(b => {
        const startDate = new Date(b.startMs);
        const endDate = new Date(b.endMs);
        return {
          startMs: b.startMs,
          endMs: b.endMs,
          startStr: startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          endStr: endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
      });
  }

  sendReminderForSelectedDay() {
    const practitionerId = this.bookingForm.get('practitioner')?.value;
    const dateObj = this.bookingForm.get('date')?.value;

    if (!practitionerId || !dateObj) return;

    const date = new Date(dateObj);
    const start = new Date(date);
    const end = new Date(date);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    const payload = {
      targetUserId: practitionerId,
      intervalType: 'Day',
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      reasonType: 'Missing'
    };

    const confirmRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Send reminder',
        message: `Send a reminder for this practitioner on ${date.toISOString().split('T')[0]}?`,
        confirmText: 'Send reminder',
        cancelText: 'Cancel'
      }
    });

    confirmRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.http.post(`${this.authBackendUrl}/api/schedules/remind-practitioner`, payload).subscribe({
        next: () => {
          const orgId = this.authService.getOrganizationId?.() || '';
          if (orgId && practitionerId) {
            this.firebaseAlertService.postUserAlert(orgId, practitionerId, {
              title: 'Practitioner schedule reminder',
              description: `No schedule slot was found for ${date.toISOString().split('T')[0]}.`,
              url: '/app/appointments',
              query: { practitionerId, date: date.toISOString().split('T')[0] }
            }).subscribe();
          }
        },
        error: () => {
          this.errorService.openandCloseError('Failed to send reminder.');
        }
      });
    });
  }

  checkOverlap() {
    this.overlapError = '';
    // Read directly from the controls because during valueChanges, the parent FormGroup's 
    // `.value` object may not be fully updated yet.
    const date = this.bookingForm.get('date')?.value;
    const time = this.bookingForm.get('time')?.value;
    const duration = this.bookingForm.get('duration')?.value;
    
    if (this.bookingForm.get('time')?.hasError('overlap')) {
      this.bookingForm.get('time')?.setErrors(null);
    }
    if (this.bookingForm.get('time')?.hasError('offDuty')) {
      this.bookingForm.get('time')?.setErrors(null);
    }
    
    if (!date || !time || !duration) return;
    
    if (this.shiftBlocks.length === 0) {
      this.overlapError = 'Practitioner is not scheduled to work on this day.';
      this.bookingForm.get('time')?.setErrors({ offDuty: true });
      return;
    }

    const isoDate = typeof date.toISOString === 'function' ? date.toISOString() : new Date(date).toISOString();
    const dateStr = isoDate.split('T')[0];
    
    const startDateTime = new Date(`${dateStr}T${time}:00`);
    const endDateTime = new Date(startDateTime.getTime() + duration * 60000);
    const reqStartMs = startDateTime.getTime();
    const reqEndMs = endDateTime.getTime();

    const withinShift = this.shiftBlocks.some(block => reqStartMs >= block.startMs && reqEndMs <= block.endMs);
    if (!withinShift) {
      this.overlapError = `The requested time falls outside the practitioner's working shift for this day.`;
      this.bookingForm.get('time')?.setErrors({ offDuty: true });
      return;
    }

    const withinFreeBlock = this.availableBlocks.some(block => reqStartMs >= block.startMs && reqEndMs <= block.endMs);
    if (!withinFreeBlock) {
      this.overlapError = `The requested time conflicts with an existing appointment.`;
      this.bookingForm.get('time')?.setErrors({ overlap: true });
      return;
    }
  }

  submitAppointment() {
    if (this.bookingForm.invalid) {
      this.errorService.openandCloseError('Please complete all form fields.');
      return;
    }

    if (!this.selectedPatientId) {
      this.errorService.openandCloseError('Please select a patient.');
      return;
    }

    const { appointmentType, date, time, duration } = this.bookingForm.value;

    const isoDate = typeof date.toISOString === 'function' ? date.toISOString() : new Date(date).toISOString();
    const dateStr = isoDate.split('T')[0];
    
    const startDateTime = new Date(`${dateStr}T${time}:00`);
    const endDateTime = new Date(startDateTime.getTime() + duration * 60000);

    const appointment: Appointment = {
      resourceType: 'Appointment',
      status: 'booked',
      start: startDateTime.toISOString(),
      end: endDateTime.toISOString(),
      minutesDuration: duration,
      serviceCategory: [{
        coding: [{
          system: `${this.terminologyUrl}/CodeSystem/appointment-service-category`,
          code: 'consultation',
          display: 'Consultation'
        }]
      }],
      appointmentType: {
        coding: [{
          system: 'http://e-likita.org/fhir/CodeSystem/appointment-mode',
          code: 'in-person',
          display: 'In-Person'
        }]
      },
      specialty: [{
        coding: [{
          system: `${this.terminologyUrl}/CodeSystem/specialties`,
          code: appointmentType,
          display: this.appointmentTypes.find((t: any) => t.code === appointmentType)?.display
        }]
      }],
      participant: [
        {
          actor: { reference: `Patient/${this.selectedPatientId}` },
          status: 'accepted'
        },
        {
          actor: { reference: `Practitioner/${this.bookingForm.value.practitioner}` },
          status: 'accepted'
        }
      ]
    };

    this.fhirResourceService.postResource('Appointment', appointment).subscribe({
      next: () => {
        this.router.navigate(['/app/appointments']);
      },
      error: () => this.errorService.openandCloseError('Failed to book appointment.')
    });
  }
}
